// useModal.tsx
import type React from "react";
import {createContext, useCallback, useContext, useState} from "react";
import ReactDOM from "react-dom";

import {faTimes} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

import CustomButton from "~/component/common/CustomButton";

// Create a context to track modal nesting level
const ModalContext = createContext<{nestingLevel: number}>({
  nestingLevel: 0,
});

const useModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const parentModalContext = useContext(ModalContext);

  const openModal = useCallback(() => setIsOpen(true), []);
  const closeModal = useCallback(() => setIsOpen(false), []);

  const Modal: React.FC<{
    children: React.ReactNode;
    preventAutoClose?: boolean;
  }> = ({children, preventAutoClose = false}) => {
    if (!isOpen) return null;

    // Get the current nesting level from parent context
    const currentNestingLevel = parentModalContext.nestingLevel;

    // Calculate the next nesting level
    const nextNestingLevel = currentNestingLevel + 1;

    // Calculate z-index based on nesting level (base z-index + 10 per level)
    const zIndex = 50 + nextNestingLevel * 10;

    // Calculate opacity (decreases slightly with each nesting level but never below 0.3)
    const opacity = Math.max(0.3, 0.7 - currentNestingLevel * 0.1);

    // Render modal content to the modal-root element via portal.
    return ReactDOM.createPortal(
      <ModalContext.Provider value={{nestingLevel: nextNestingLevel}}>
        <div
          className="fixed inset-0 flex items-center justify-center bg-black"
          style={{
            backgroundColor: "rgba(0, 0, 0, " + opacity + ")",
            zIndex: zIndex,
          }}
          onClick={preventAutoClose ? undefined : closeModal}
        >
          <div
            className="bg-gray-900 rounded-lg p-6 relative max-h-[95vh] overflow-y-auto min-w-[300px]"
            onClick={(e) => e.stopPropagation()}
          >
            <CustomButton
              onClick={closeModal}
              aria-label="Close modal"
              className="absolute top-3 right-4 text-gray-400 hover:text-white transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} size="2xl" />
            </CustomButton>
            {children}
          </div>
        </div>
      </ModalContext.Provider>,
      document.getElementById("modal-root")!,
    );
  };

  return {isOpen, openModal, closeModal, Modal};
};

export default useModal;
